import {
  Box,
  Button,
  ButtonGroup,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ClientOnboardingApi from '../../../apis/ClientOnboardingApi';
// import { ReactTyped } from 'react-typed';
import City from '../../../lib/City';
import useApi from '../../../lib/useApi';
import useSharedAuthLib from '../../../lib/useSharedAuthLib';
import { HeroDarkBgColor, HeroLightBgColor } from '../HomeView';

/** The hero section for the home page */
const HeroSection = () => {
  const authLib = useSharedAuthLib();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });
  const clientOnboardingApi = useApi(ClientOnboardingApi);
  const [isLoading, setIsLoading] = useState(true);
  const [errorLoading, setErrorLoading] = useState(false);
  const [startingPrice, setStartingPrice] = useState<string>();

  /** Use effect to get starting price for the licenses */
  useEffect(() => {
    // Setup
    setIsLoading(true);
    setErrorLoading(false);
    const load = async () => {
      const licenseTiers = await clientOnboardingApi.GetLicenseTiers();
      setStartingPrice(
        licenseTiers[0].yearlyPrice.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    };

    // Load the starting price from the backend
    load()
      .catch(() => {
        setErrorLoading(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [clientOnboardingApi]);

  return (
    <Grid
      container
      sx={{
        backgroundColor: (t) =>
          t.palette.mode == 'dark' ? HeroDarkBgColor : HeroLightBgColor,
        paddingY: 10,
      }}
      rowSpacing={5}
      columnSpacing={2}
    >
      <Grid item container alignItems="center" xs={12} md={8}>
        <Box width="100%">
          <Box marginBottom={5}>
            <Typography
              variant="h2"
              color="textPrimary"
              sx={{
                fontWeight: 700,
                fontSize: 45,
                marginBottom: 3,
              }}
            >
              Online Training for Crisis Response & Prevention
            </Typography>
            <Typography
              sx={{
                fontSize: 23,
                fontWeight: 600,
                marginBottom: 0.5,
              }}
            >
              Prepare your team. Empower your people. Protect your workplace.
            </Typography>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 300,
                minHeight: 30,
              }}
            >
              {isLoading
                ? ''
                : errorLoading
                  ? 'Annual access to training for your organization.'
                  : `Annual access to training starting at $${startingPrice} per student.`}
            </Typography>
          </Box>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretched', sm: 'flex-start' }}
          >
            <Button
              size="large"
              variant="contained"
              color="primary"
              fullWidth={isMd ? false : true}
              component={Link}
              to="/purchase-training"
            >
              Purchase Training
            </Button>
            <Box
              marginTop={{ xs: 2, sm: 0 }}
              marginLeft={{ sm: 2 }}
              width={{ xs: '100%', md: 'auto' }}
            >
              {authLib.AuthState.Authenticated ? (
                <Button
                  fullWidth={isMd ? false : true}
                  onClick={authLib.Logout}
                  variant="outlined"
                  size="large"
                  color="secondary"
                >
                  Logout
                </Button>
              ) : (
                <ButtonGroup
                  variant="outlined"
                  color="secondary"
                  size="large"
                  fullWidth={isMd ? false : true}
                >
                  <Button component={Link} to="/login">
                    Login
                  </Button>
                  <Button component={Link} to="/signup">
                    Signup
                  </Button>
                </ButtonGroup>
              )}
            </Box>
          </Box>
        </Box>
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Box height="100%" width="100%" maxWidth="500px">
          <City height="100%" width="100%" />
        </Box>
      </Grid>
    </Grid>
  );
};

export default HeroSection;
